import React from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { getScoreTone } from '../utils/formatters';

const PreviewPhase = ({ preview, previewRevealStage, pillarCards, beginUnlockFlow }) => {
  const overallTone = getScoreTone(preview.overall);

  return (
    <motion.div key="preview" className="report-stage final-report" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="report-header">
        <CheckCircle2 size={40} className="check-icon" />
        <h3>Your competition-adjusted grade is in</h3>
      </div>

      {/* Hero teaser: big grade ring + headline */}
      <div className="grade-hero">
        <div className={`grade-ring score-${overallTone}`} style={{ '--grade-pct': preview.overall }}>
          <div className="grade-ring__value">{preview.overall}</div>
          <div className="grade-ring__grade">{preview.letterGrade}</div>
          <div className="grade-ring__label">out of 100</div>
        </div>
        <div className="grade-hero__body">
          <h4 className="grade-hero__title">Competition-Adjusted Grade</h4>
          <p className="grade-hero__sub">
            Rank <strong>#{preview.rank?.position || '-'} of {preview.rank?.total || '-'}</strong> competitors
            {' · '}{preview.percentile || 0}th percentile{' · '}{preview.tier}
          </p>
        </div>
      </div>

      {/* Pillar score cards */}
      <div className={`report-section reveal-block ${previewRevealStage >= 1 ? 'is-visible' : ''}`}>
        <p className="report-section__title">Pillar scores</p>
        <div className="metrics-grid">
          {pillarCards.map((pillar) => (
            <div className={`metric-card score-${getScoreTone(pillar.value)}`} key={pillar.key}>
              <div className="metric-value">{pillar.value}/100</div>
              <div className="metric-label">{pillar.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Module score teaser */}
      {preview.modules?.target && (
        <div className={`report-section reveal-block ${previewRevealStage >= 2 ? 'is-visible' : ''}`}>
          <p className="report-section__title">Module signals</p>
          <div className="scan-coverage-grid" aria-label="Module score details">
            <div className="scan-coverage-card">
              <small>Review Sentiment</small>
              <strong className={`score-value score-${getScoreTone(preview.modules.target.reviewSentiment?.score || 0)}`}>{preview.modules.target.reviewSentiment?.score || 0}/100</strong>
            </div>
            <div className="scan-coverage-card">
              <small>Review Samples</small>
              <strong>{preview.modules.target.reviewSentiment?.sampleCount || 0}</strong>
            </div>
            <div className="scan-coverage-card">
              <small>Photo Quality</small>
              <strong className={`score-value score-${getScoreTone(preview.modules.target.photoQuality?.score || 0)}`}>{preview.modules.target.photoQuality?.score || 0}/100</strong>
            </div>
            <div className="scan-coverage-card">
              <small>Photo Count</small>
              <strong>{preview.modules.target.photoQuality?.photoCount || 0}</strong>
            </div>
          </div>
          {preview.limitedCompetitionData && (
            <p className="booking-note">Competitor sample is limited, so rank percentile is provisional.</p>
          )}
        </div>
      )}

      {/* Top issues */}
      <div className={`report-section reveal-block ${previewRevealStage >= 2 ? 'is-visible' : ''}`}>
        <p className="report-section__title">We found {preview.issueCount} issues — here are 3</p>
        <ul className="preview-issues-list">
          {(preview.issueExamples || []).map((issue) => (
            <li key={issue.title}>
              <AlertTriangle size={14} />
              <div>
                <strong>{issue.title}</strong>
                <p>{issue.detail}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Competitors ahead + AI line + annual upside */}
      <div className={`report-summary reveal-block ${previewRevealStage >= 2 ? 'is-visible' : ''}`}>
        <p><strong>Who&apos;s beating you on Google:</strong> {preview.competitorCount} competitors in your local cohort.</p>
        {preview.aiInsights?.summary && (
          <p><strong>AI insight:</strong> {preview.aiInsights.summary}</p>
        )}
        <p>Closing these gaps could increase sales by about <strong>${preview.annualUpside.toLocaleString()}/year</strong>.</p>
      </div>

      {/* Unlock CTA */}
      <div className={`report-cta reveal-block ${previewRevealStage >= 3 ? 'is-visible' : ''}`}>
        <p className="report-cta__copy">
          See the full competitor league table, module-by-module audit, and your prioritized
          action plan to <strong>close the gap and out-rank your local market</strong>.
        </p>
        <button
          className="btn-try-again"
          type="button"
          onClick={beginUnlockFlow}
        >
          Unlock full competitor report
        </button>
      </div>
    </motion.div>
  );
};

export default PreviewPhase;
